import {
    ModerationFeedbackType,
    type FeedbackType,
    type LegacyModerationFeedbackType,
    type PostType,
    type ProjectFeedbackType,
} from "../types.ts";

type GqlFeedbackTypeTopicQuestions =
    | "ALL_PROJECT_ANSWER"
    | "ALL_PROJECT_QUESTION"
    | "ANSWER"
    | "COMMENT"
    | "PROGRAM"
    | "PROJECTFEEDBACK"
    | "PROJECT_CRITIQUE_ANSWER"
    | "PROJECT_CRITIQUE_QUESTION"
    | "PROJECT_EVAL_ANSWER"
    | "PROJECT_EVAL_QUESTION"
    | "PROJECT_HELP_ANSWER"
    | "PROJECT_HELP_QUESTION"
    | "QUESTION"
    | "REPLY"
    | "SUGGESTED_CLARIFICATION";

export const toGqlFeedbackType = (
    feedbackType: FeedbackType | ProjectFeedbackType | PostType,
): GqlFeedbackTypeTopicQuestions => {
    switch (feedbackType) {
        case "questions":
        case "question":
            return "QUESTION";
        case "answers":
        case "answer":
            return "ANSWER";
        case "clarifications":
        case "clarification":
            return "SUGGESTED_CLARIFICATION";
        case "comments":
        case "comment":
            return "COMMENT";
        case "replies":
        case "reply":
            return "REPLY";
        case "projectcritiqueanswer":
            return "PROJECT_CRITIQUE_ANSWER";
        case "projectcritique":
            return "PROJECT_CRITIQUE_QUESTION";
        case "projecteval":
            return "PROJECT_EVAL_QUESTION";
        case "projectevalanswer":
            return "PROJECT_EVAL_ANSWER";
        case "projectfeedback":
            return "PROJECTFEEDBACK";
        case "projecthelp":
            return "PROJECT_HELP_QUESTION";
        case "projecthelpanswer":
            return "PROJECT_HELP_ANSWER";
        case "scratchpad":
            return "PROGRAM";
        default:
            return "QUESTION";
    }
};

export function fromGqlToDiscussionType(
    gqlType: GqlFeedbackTypeTopicQuestions,
): FeedbackType | null | undefined | ProjectFeedbackType | null | undefined {
    switch (gqlType) {
        case "ANSWER":
            return "answers";
        case "COMMENT":
            return "comments";
        case "QUESTION":
            return "questions";
        case "REPLY":
            return "replies";
        case "PROJECT_CRITIQUE_ANSWER":
            return "projectcritiqueanswer";
        case "PROJECT_CRITIQUE_QUESTION":
            return "projectcritique";
        case "PROJECT_EVAL_ANSWER":
            return "projectevalanswer";
        case "PROJECT_EVAL_QUESTION":
            return "projecteval";
        case "PROJECTFEEDBACK":
            return "projectfeedback";
        case "PROJECT_HELP_ANSWER":
            return "projecthelpanswer";
        case "PROJECT_HELP_QUESTION":
            return "projecthelp";
        case "SUGGESTED_CLARIFICATION":
            return "clarifications";
        case "PROGRAM":
        case "ALL_PROJECT_QUESTION":
        case "ALL_PROJECT_ANSWER":
            throw new Error(`"${gqlType}" is not a valid feedback type!`);
    }
}

export const fromGqlToPostType = (
    type: GqlFeedbackTypeTopicQuestions | string,
): PostType => {
    const feedbackType = type as GqlFeedbackTypeTopicQuestions;
    if (feedbackType) {
        return _feedbackToPostSwitch(feedbackType);
    }
    throw new Error(`"${type as string}" is not a valid post type!`);
};

const _feedbackToPostSwitch = (
    feedbackType: GqlFeedbackTypeTopicQuestions,
): PostType => {
    switch (feedbackType) {
        case "ANSWER":
            return "answer";
        case "COMMENT":
            return "comment";
        case "QUESTION":
            return "question";
        case "REPLY":
            return "reply";
        case "PROJECT_EVAL_ANSWER":
            return "projectevalanswer";
        case "PROJECT_EVAL_QUESTION":
            return "projecteval";
        case "PROJECT_HELP_QUESTION":
            return "projecthelp";
        case "PROJECT_HELP_ANSWER":
            return "projecthelpanswer";
        case "SUGGESTED_CLARIFICATION":
            return "clarification";
        case "PROGRAM":
            return "scratchpad";
        // using a default on enums prevents exhaustive check, which helps
        // prevent bugs from changes to the schema
        case "PROJECTFEEDBACK":
        case "PROJECT_CRITIQUE_ANSWER":
        case "PROJECT_CRITIQUE_QUESTION":
        case "ALL_PROJECT_QUESTION":
        case "ALL_PROJECT_ANSWER":
            throw new Error(`"${feedbackType}" is not a valid post type!`);
    }
};

export const fromLegacyModTypeToEnum = (
    type: LegacyModerationFeedbackType | PostType,
): ModerationFeedbackType => {
    switch (type) {
        case "answer":
            return ModerationFeedbackType.ANSWER;
        case "comment":
            return ModerationFeedbackType.COMMENT;
        case "question":
            return ModerationFeedbackType.QUESTION;
        case "reply":
            return ModerationFeedbackType.REPLY;
        case "projecthelp":
            return ModerationFeedbackType.PROJECT_HELP_QUESTION;
        case "project":
        case "scratchpad":
            return ModerationFeedbackType.PROGRAM;
    }
    throw new Error(
        `"${type as string}" is not a valid moderation feedback type!`,
    );
};

type GqlFeedbackTypeFlaggedFeedback =
    | "ALL_PROJECT_ANSWER"
    | "ALL_PROJECT_QUESTION"
    | "ANSWER"
    | "COMMENT"
    | "PROGRAM"
    | "PROJECTFEEDBACK"
    | "PROJECT_CRITIQUE_ANSWER"
    | "PROJECT_CRITIQUE_QUESTION"
    | "PROJECT_EVAL_ANSWER"
    | "PROJECT_EVAL_QUESTION"
    | "PROJECT_HELP_ANSWER"
    | "PROJECT_HELP_QUESTION"
    | "QUESTION"
    | "REPLY"
    | "SUGGESTED_CLARIFICATION";

export const fromModerationToGqlType = (
    type: ModerationFeedbackType | string,
): GqlFeedbackTypeFlaggedFeedback => {
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"COMMENT" | "QUESTION" | "PROJECT_HELP_QUESTION" | "REPLY" | "ANSWER" | "PROGRAM"'.
    const enumType = Object.values(ModerationFeedbackType).includes(type)
        ? type
        : undefined;
    if (enumType) {
        switch (enumType) {
            case ModerationFeedbackType.ANSWER:
                return "ANSWER";
            case ModerationFeedbackType.COMMENT:
                return "COMMENT";
            case ModerationFeedbackType.QUESTION:
                return "QUESTION";
            case ModerationFeedbackType.REPLY:
                return "REPLY";
            case ModerationFeedbackType.PROJECT_HELP_QUESTION:
                return "PROJECT_HELP_QUESTION";
            case ModerationFeedbackType.PROGRAM:
                return "PROGRAM";
        }
    }
    throw new Error(`"${type as string}" is not a valid GQL feedback type!`);
};
